import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { Endpoint } from "src/app/shared/endpoints";
import { OrgAccessRoleDefinition } from "src/app/shared/org-access-roles";

export interface OnboardTeamInput {
  teamId?: string;
  name: string;
  adminEmail: string;
  delete?: boolean;
  /** FE-only: comma/newline emails to invite as Workers after save. */
  memberInviteEmails?: string;
}

export interface OnboardApplicationInput {
  applicationId?: string;
  name: string;
  adminEmail: string;
  teams: OnboardTeamInput[];
  delete?: boolean;
}

export interface OrgTerminologyModel {
  roleLabel?: string;
  statusLabel?: string;
  customRulesLabel?: string;
  departmentLabel?: string;
  locationLabel?: string;
  divisionLabel?: string;
  shiftLabel?: string;
  applicationLabel?: string;
  teamLabel?: string;
  /** Optional org-defined option values (may be empty). */
  roles?: string[];
  statuses?: string[];
  customRules?: string[];
  departments?: string[];
  locations?: string[];
  divisions?: string[];
  shifts?: string[];
  /** Built-in access roles (enable/disable + features). */
  accessRoles?: OrgAccessRoleDefinition[];
}

export interface OrgBrandingModel {
  logoUrl?: string;
  /** Light-mode primary hex — replaces Flowchest blue (#5EA1D8). */
  primaryColor?: string;
  accentColor?: string;
  surfaceColor?: string;
  /** Dark-mode primary hex — replaces Flowchest blue in dark theme. */
  darkPrimaryColor?: string;
  darkAccentColor?: string;
}

export interface OrganizationOnboardRequest {
  organizationName: string;
  primaryAdminEmail: string;
  /** Invited as OrgAdmin (same access as the creator) once setup is saved. */
  alternateAdminEmail?: string;
  applications: OnboardApplicationInput[];
  terminology?: OrgTerminologyModel;
  branding?: OrgBrandingModel;
}

export interface AcceptInviteRequest {
  employeeId?: string;
  referralCode?: string;
  division?: string;
  department?: string;
  location?: string;
  shift?: string;
}

export interface EmployeeProfileModel {
  organizationId?: string;
  jobRole?: string;
  employeeId?: string;
  referralCode?: string;
  division?: string;
  department?: string;
  location?: string;
  shift?: string;
}

export type UpdateEmployeeProfileRequest = AcceptInviteRequest;

export interface InviteRegistrationOptions {
  departmentLabel?: string;
  locationLabel?: string;
  divisionLabel?: string;
  shiftLabel?: string;
  departments?: string[];
  locations?: string[];
  divisions?: string[];
  shifts?: string[];
}

/** sessionStorage key for employee details collected during invite-linked signup. */
export function orgInviteEmployeeStorageKey(inviteId: string): string {
  return `orgInviteEmployee:${inviteId}`;
}

@Injectable({ providedIn: "root" })
export class OrganizationService {
  private readonly base = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  onboard(body: OrganizationOnboardRequest): Observable<any> {
    return this.http.post(
      `${this.base}${Endpoint.organizationOnboardUrl}`,
      body
    );
  }

  updateSetup(organizationId: string, body: OrganizationOnboardRequest): Observable<any> {
    return this.http.put(
      `${this.base}${Endpoint.organizationByIdUrl}/${organizationId}/setup`,
      body
    );
  }

  getContext(
    organizationId?: string,
    mode?: "public" | "organization"
  ): Observable<any> {
    const params: string[] = [];
    if (organizationId) {
      params.push(`organizationId=${encodeURIComponent(organizationId)}`);
    }
    if (mode === "public") {
      params.push("mode=public");
    }
    const q = params.length ? `?${params.join("&")}` : "";
    return this.http.get(
      `${this.base}${Endpoint.organizationContextUrl}${q}`
    );
  }

  getOrganization(organizationId: string): Observable<any> {
    return this.http.get(
      `${this.base}${Endpoint.organizationByIdUrl}/${organizationId}`
    );
  }

  updateOrganization(
    organizationId: string,
    body: {
      name?: string;
      primaryAdminEmail?: string;
      terminology?: OrgTerminologyModel;
      branding?: OrgBrandingModel;
      contentAccessScope?: string;
      allowPublicContentPublish?: boolean;
      memberAccountAccess?: string;
    }
  ): Observable<any> {
    return this.http.put(
      `${this.base}${Endpoint.organizationByIdUrl}/${organizationId}`,
      body
    );
  }

  listApplications(organizationId: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.base}${Endpoint.organizationByIdUrl}/${organizationId}/applications`
    );
  }

  listTeams(organizationId: string, applicationId?: string): Observable<any[]> {
    const q = applicationId
      ? `?applicationId=${encodeURIComponent(applicationId)}`
      : "";
    return this.http.get<any[]>(
      `${this.base}${Endpoint.organizationByIdUrl}/${organizationId}/teams${q}`
    );
  }

  createApplication(
    organizationId: string,
    body: { name: string; adminEmail: string; contentAccessScope?: string }
  ): Observable<any> {
    return this.http.post(
      `${this.base}${Endpoint.organizationByIdUrl}/${organizationId}/applications`,
      body
    );
  }

  updateApplication(
    organizationId: string,
    applicationId: string,
    body: { name: string; adminEmail: string; contentAccessScope?: string }
  ): Observable<any> {
    return this.http.put(
      `${this.base}${Endpoint.organizationByIdUrl}/${organizationId}/applications/${applicationId}`,
      body
    );
  }

  deleteApplication(organizationId: string, applicationId: string): Observable<any> {
    return this.http.delete(
      `${this.base}${Endpoint.organizationByIdUrl}/${organizationId}/applications/${applicationId}`
    );
  }

  createTeam(
    organizationId: string,
    applicationId: string,
    body: { name: string; adminEmail: string; contentAccessScope?: string }
  ): Observable<any> {
    return this.http.post(
      `${this.base}${Endpoint.organizationByIdUrl}/${organizationId}/applications/${applicationId}/teams`,
      body
    );
  }

  updateTeam(
    organizationId: string,
    applicationId: string,
    teamId: string,
    body: { name: string; adminEmail: string; contentAccessScope?: string }
  ): Observable<any> {
    return this.http.put(
      `${this.base}${Endpoint.organizationByIdUrl}/${organizationId}/applications/${applicationId}/teams/${teamId}`,
      body
    );
  }

  deleteTeam(
    organizationId: string,
    applicationId: string,
    teamId: string
  ): Observable<any> {
    return this.http.delete(
      `${this.base}${Endpoint.organizationByIdUrl}/${organizationId}/applications/${applicationId}/teams/${teamId}`
    );
  }

  getTerminology(organizationId: string): Observable<any> {
    return this.http.get(
      `${this.base}${Endpoint.organizationByIdUrl}/${organizationId}/terminology`
    );
  }

  updateTerminology(
    organizationId: string,
    body: OrgTerminologyModel
  ): Observable<any> {
    return this.http.put(
      `${this.base}${Endpoint.organizationByIdUrl}/${organizationId}/terminology`,
      body
    );
  }

  getBranding(organizationId: string): Observable<any> {
    return this.http.get(
      `${this.base}${Endpoint.organizationByIdUrl}/${organizationId}/branding`
    );
  }

  uploadLogo(formData: FormData): Observable<any> {
    return this.http.post(
      `${this.base}${environment.uploadImageUrl}`,
      formData
    );
  }

  invite(body: {
    organizationId: string;
    applicationId?: string;
    teamId?: string;
    email: string;
    role?: string;
    jobRole?: string;
    scope?: string;
  }): Observable<any> {
    return this.http.post(`${this.base}api/organization/invite`, body);
  }

  bulkInvite(body: {
    organizationId: string;
    applicationId?: string;
    teamId?: string;
    scope?: string;
    defaultRole?: string;
    defaultJobRole?: string;
    invites: { email: string; role?: string; jobRole?: string }[];
  }): Observable<any> {
    return this.http.post(`${this.base}api/organization/invites/bulk`, body);
  }

  myInvites(): Observable<any[]> {
    return this.http.get<any[]>(`${this.base}api/organization/invites/mine`);
  }

  listOrgInvites(organizationId: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.base}api/organization/${organizationId}/invites`
    );
  }

  acceptInvite(
    organizationId: string,
    inviteId: string,
    body?: AcceptInviteRequest | null
  ): Observable<any> {
    return this.http.post(
      `${this.base}api/organization/${organizationId}/invites/${inviteId}/accept`,
      body || {}
    );
  }

  getMyEmployeeProfile(organizationId: string): Observable<EmployeeProfileModel> {
    return this.http.get<EmployeeProfileModel>(
      `${this.base}api/organization/${organizationId}/my-employee-profile`
    );
  }

  updateMyEmployeeProfile(
    organizationId: string,
    body: UpdateEmployeeProfileRequest
  ): Observable<EmployeeProfileModel> {
    return this.http.put<EmployeeProfileModel>(
      `${this.base}api/organization/${organizationId}/my-employee-profile`,
      body || {}
    );
  }

  getInviteRegistrationOptions(
    organizationId: string,
    inviteId: string
  ): Observable<InviteRegistrationOptions> {
    return this.http.get<InviteRegistrationOptions>(
      `${this.base}api/organization/${organizationId}/invites/${inviteId}/registration-options`
    );
  }

  listDirectory(): Observable<any[]> {
    return this.http.get<any[]>(`${this.base}api/organization/directory`);
  }

  requestJoinTeam(body: {
    organizationId: string;
    applicationId: string;
    teamId: string;
  }): Observable<any> {
    return this.http.post(`${this.base}api/organization/join-requests`, body);
  }

  listJoinRequests(organizationId: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.base}api/organization/${organizationId}/join-requests`
    );
  }

  respondJoinRequest(
    organizationId: string,
    requestId: string,
    body: { accept: boolean; role?: string; jobRole?: string }
  ): Observable<any> {
    return this.http.post(
      `${this.base}api/organization/${organizationId}/join-requests/${requestId}/respond`,
      body
    );
  }

  getOwnedContent(organizationId: string, email: string): Observable<any> {
    return this.http.get(
      `${this.base}api/organization/${organizationId}/members/${encodeURIComponent(
        email
      )}/owned-content`
    );
  }

  discontinueMember(body: {
    organizationId: string;
    email: string;
    reassignments: {
      contentType: string;
      rowKey: string;
      newOwnerEmail: string;
    }[];
  }): Observable<any> {
    return this.http.post(
      `${this.base}api/organization/members/discontinue`,
      body
    );
  }

  listLibrary(
    organizationId: string,
    applicationId?: string,
    teamId?: string,
    publishedOnly = false
  ): Observable<any[]> {
    const params: string[] = [];
    if (applicationId)
      params.push(`applicationId=${encodeURIComponent(applicationId)}`);
    if (teamId) params.push(`teamId=${encodeURIComponent(teamId)}`);
    if (publishedOnly) params.push("publishedOnly=true");
    const q = params.length ? `?${params.join("&")}` : "";
    return this.http.get<any[]>(
      `${this.base}api/organization/${organizationId}/library${q}`
    );
  }

  /** Scoped member list for Org/App/Team admins (search Users tab). */
  listMembers(
    organizationId: string,
    opts?: {
      applicationId?: string;
      teamId?: string;
      q?: string;
      pageIndex?: number;
      pageSize?: number;
    }
  ): Observable<{
    members: any[];
    totalCount: number;
    pageIndex: number;
    pageSize: number;
  }> {
    const params: string[] = [];
    if (opts?.applicationId)
      params.push(`applicationId=${encodeURIComponent(opts.applicationId)}`);
    if (opts?.teamId) params.push(`teamId=${encodeURIComponent(opts.teamId)}`);
    if (opts?.q) params.push(`q=${encodeURIComponent(opts.q)}`);
    if (opts?.pageIndex != null)
      params.push(`pageIndex=${encodeURIComponent(String(opts.pageIndex))}`);
    if (opts?.pageSize != null)
      params.push(`pageSize=${encodeURIComponent(String(opts.pageSize))}`);
    const q = params.length ? `?${params.join("&")}` : "";
    return this.http.get<{
      members: any[];
      totalCount: number;
      pageIndex: number;
      pageSize: number;
    }>(`${this.base}api/organization/${organizationId}/members${q}`);
  }

  updateMemberRole(
    organizationId: string,
    body: {
      email: string;
      role: string;
      jobRole?: string | null;
      applicationId?: string | null;
      teamId?: string | null;
    }
  ): Observable<any> {
    return this.http.put(
      `${this.base}api/organization/${organizationId}/members/role`,
      body
    );
  }

  /** Org/App/Team admin catalog for User Dashboard Organization content. */
  listAdminContent(
    organizationId: string,
    applicationId?: string,
    teamId?: string
  ): Observable<any[]> {
    const params: string[] = [];
    if (applicationId)
      params.push(`applicationId=${encodeURIComponent(applicationId)}`);
    if (teamId) params.push(`teamId=${encodeURIComponent(teamId)}`);
    const q = params.length ? `?${params.join("&")}` : "";
    return this.http.get<any[]>(
      `${this.base}api/organization/${organizationId}/content${q}`
    );
  }

  listFeedback(organizationId: string): Observable<any[]> {
    return this.http.get<any[]>(
      `${this.base}api/organization/${organizationId}/feedback`
    );
  }
}
